import { Locale } from '@/common'
import Data from '@/data'
import Trans, { Translation } from '@/data/trans'
import { itemFixSockets, itemTranslationDomain } from '@/logic/item'
import { StatType } from '@/types/item'
import { underlinerFunc } from '@/ui/select'
import Tooltip from '@/ui/tooltip'
import { replaceReact } from '@/utils/format'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SvgIcon } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React from 'react'
import HoverNumber from '../editor/HoverNumber'
import { ItemProps } from './ItemEditor'
import { StatsChanger, TextLineSelect } from './TextLine'
import { AbstractCategory, categoryNames } from './category'
import { PassiveSelector } from './passives'
import { SelectLine } from './select'

function StatsTextLine({
  domain,
  id,
  stats,
  onChange,
  expanded
}: {
  domain: string
  id: number
  stats: Record<string, number>
  onChange: (value: StatsChanger) => void
  expanded?: boolean
}) {
  const stat = Trans[domain][id]
  const rule = stat.generic()
  if (stat[Locale].length > 1 && rule.format.every((s) => s === 'ignore')) {
    return <TextLineSelect domain={domain} id={id} stats={stats} onChange={onChange} expanded={expanded} />
  }
  if (!rule) return null
  return (
    <div>
      {replaceReact(rule.string, /{(\d+)}/g, (m, index) => {
        index = parseInt(index)
        const format = rule.format[index]
        const handler = rule.index_handlers[index]
        const statId = stat.ids[index]
        const onChangeValue = (value: number) =>
          onChange((stats) => {
            value = handler.type ? value : Math.round(handler.reverse(value))
            stats = { ...stats }
            stats[statId] = value
            stat.ids.forEach((id, index) => {
              if (rule.format[index] === 'ignore' && rule.condition[index].max != null) {
                stats[id] = rule.condition[index].max!
              }
            })
            return stats
          })

        if (handler.type) {
          return <PassiveSelector value={stats[statId] ?? 0} onChange={onChangeValue} disabled={expanded} />
        }
        const value = handler.apply(stats[statId] ?? 0) as number
        return (
          <>
            {!!format.includes('+') && '+'}
            <HoverNumber step={handler.step} value={value} onChange={onChangeValue} disabled={expanded} />
            {!!format.includes('%') && '%'}
          </>
        )
      })}
    </div>
  )
}

type StatOption = {
  id: number
  name: string
  disabled?: string
}

function makeOption(id: number, domain: string) {
  const stat = Trans[domain][id]
  const rule = stat.generic()
  if (!rule) return { id, name: 'unknown' }
  const text = rule.string.replace(/{(\d+)}/g, (m, index) => {
    index = parseInt(index)
    return rule.format[index]
  })
  return { id, name: text }
}

export function StatLine({
  domain,
  id,
  stats,
  onChange
}: {
  domain: string
  id?: number
  stats: Record<string, number>
  onChange: (value: StatsChanger) => void
}) {
  const onRemove = React.useCallback(
    () =>
      onChange((stats) => {
        stats = { ...stats }
        const prev = id != null && Trans[domain][id]
        if (prev) {
          for (const s of prev.ids) delete stats[s]
        }
        return stats
      }),
    [id, onChange, domain]
  )
  const onSelect = React.useCallback(
    (opt?: StatOption) =>
      onChange((stats) => {
        stats = { ...stats }
        const prev = id != null && Trans[domain][id]
        const values: number[] = []
        if (prev) {
          prev.ids.forEach((id, index) => {
            values[index] = stats[id]
            delete stats[id]
          })
        }
        const next = opt && Trans[domain][opt.id]
        if (next) {
          const rule = next.generic()
          next.ids.forEach((id, index) => {
            const orig = rule.format[index] === 'ignore' ? null : values[index]
            stats[id] = orig ?? rule.condition[index]?.max ?? rule.condition[index]?.min ?? 1
          })
        }
        return stats
      }),
    [id, onChange, domain]
  )

  const renderOption = React.useCallback((opt: StatOption, search?: RegExp) => {
    const option = <span>{search ? underlinerFunc(opt.name, search) : opt.name}</span>
    if (opt.disabled) {
      return (
        <Tooltip tooltip={opt.disabled} align="left">
          {option}
        </Tooltip>
      )
    } else {
      return option
    }
  }, [])

  const getOptions = React.useCallback(
    () =>
      Object.entries(Trans[domain]).map(([tid_s, stat]: [string, Translation]) => {
        const tid = Number(tid_s)
        const opt: StatOption = makeOption(tid, domain)
        if (id !== tid && stat.ids.some((id) => stats[id])) {
          opt.disabled = 'Stat already present on the item'
        }
        return opt
      }),
    [id, stats, domain]
  )

  return (
    <li className={classNames('poe-StatLine', { 'poe-empty': id == null })}>
      {id != null && (
        <span className="poe-remove" title="Remove stat" onClick={onRemove}>
          <FontAwesomeIcon icon={faTimes} />
        </span>
      )}
      <SelectLine options={getOptions} renderOption={renderOption} id={id} onChange={onSelect}>
        {(expanded) =>
          id != null ? (
            <>
              <StatsTextLine id={id} stats={stats} onChange={onChange} expanded={expanded} domain={domain} />
              {!expanded && (
                <div className="poe-mods-info">
                  <SvgIcon icon="chevron-down" className="poe-arrow" />
                </div>
              )}
            </>
          ) : (
            <div>{'<Add Stat>'}</div>
          )
        }
      </SelectLine>
    </li>
  )
}

export function StatCategory({
  item,
  onChange,
  type
}: {
  type: StatType | 'custom'
} & ItemProps) {
  const onChangeStats = React.useCallback(
    (value: StatsChanger) =>
      onChange((item) => {
        const prev = item.stats[type] || {}
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return item
        item = { ...item }
        item.stats = { ...item.stats }
        item.stats[type] = value
        return itemFixSockets(item)
      }),
    [type, onChange]
  )
  const onChangeVeiled = React.useCallback(
    (value: number) => onChange((item) => ({ ...item, veiledMods: value })),
    [onChange]
  )
  const domain = itemTranslationDomain(Data.items[item.base])
  const lines = React.useMemo(
    () => Object.keys(Trans[domain].maskFromStatList(item.stats[type] || {})).map(Number),
    [item, type, domain]
  )
  return (
    <AbstractCategory name={categoryNames[type]}>
      {lines.map((id) => (
        <StatLine key={id} stats={item.stats[type] || {}} onChange={onChangeStats} id={id} domain={domain} />
      ))}
      <StatLine stats={item.stats[type] || {}} onChange={onChangeStats} domain={domain} />
      {type === 'explicit' && (
        <li className="poe-StatLine poe-empty">
          <div className="poe-stat-contents">
            <div>
              Veiled Affixes:{' '}
              <HoverNumber min={0} max={3} step={1} value={item.veiledMods || 0} onChange={onChangeVeiled} />
            </div>
          </div>
        </li>
      )}
    </AbstractCategory>
  )
}
